import axios, {
  type AxiosError,
  type AxiosProgressEvent,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";
import { ApiError, isApiError, RELOGIN_REQUIRED_CODES } from "./errors";
import {
  classifyRenewalFailure,
  RenewalBackoff,
  RenewalFailed,
  setReconnecting,
  withRenewalLock,
} from "./renewal";
import type { ApiFailure, ApiSuccess, OrganisationChanged, RefreshResult } from "./types";

/**
 * The ONE HTTP client (rules/01 §2). Cookie session (withCredentials), CSRF header on every
 * state-changing request, envelope unwrapping, error-code mapping, the organisation pin, and
 * single-flight 401 renewal: the first 401 renews once, concurrent callers wait on the same promise,
 * every waiting request is retried exactly once (`_retry`), never on a public path (no reload loops).
 *
 * **Only a definite "no" ends a session** (ADR-0011 addendum 2026-10-04, plans/v2/stay-signed-in.md
 * §5.2). Every sign-in costs an SMS, so the renewal's own answer is classified (renewal.ts):
 *  - 401 → `sessionLost(reason)`: sign-in, saying why (`MEMBERSHIP_INACTIVE`, `TENANT_DELETED`).
 *  - no answer, timeout, 408, 429, 5xx → the session stays; the waiting requests fail with that blip,
 *    never with the 401 that would send `MeGate` to sign-in; "Reconnecting…" shows; the next renewal
 *    waits 2 s, 5 s, 15 s, 30 s, then every 60 s (`Retry-After` honoured), retried on its own and at
 *    once when the network comes back or the tab is shown again.
 *  - any other 4xx → the session stays and the request fails with `SESSION_RENEWAL_REFUSED`.
 *
 * **One renewal per browser** (§5.4): the renewal holds the `nacs-refresh` Web Lock. A tab that finds it
 * held waits, then re-tries its own request once before renewing — the other tab has usually renewed the
 * shared cookie already.
 *
 * **Every request keeps its organisation** (§5.6): `X-Nacs-Org` is stamped when the request is created
 * and kept on its retry, so a request made while the tab showed A is never written into B. A renewal
 * that moved the session to another organisation (`organisationChanged`) replays nothing: the waiting
 * requests fail with `ORGANISATION_CHANGED` and the move handler gates the screen. A 409 `ORG_MISMATCH`
 * (another tab switched or moved this browser) is dropped the same way, and the tab follows the session.
 */
const API_PREFIX = "/api/v1";
const REQUEST_TIMEOUT_MS = 15_000;
const UPLOAD_TIMEOUT_MS = 30_000;
const PUBLIC_PATHS = ["/login", "/kitchen-sink", "/legal", "/whats-new", "/account-deletion"];
/**
 * Public pages matched EXACTLY (a trailing slash aside), because as a prefix they would also match signed-in
 * pages: `/v`, the visitor pass link (`/v?<code>`, plan visitor-pass-link decision 12), is a prefix of
 * `/visitors` and `/visits`. M27's `/display` (a paired wall screen, which authenticates with its own token) is a
 * prefix of `/displays`, and `/display/x` is nothing public.
 */
const PUBLIC_EXACT_PATHS = ["/v", "/display"];
/** A 401 from these is an answer, not an expired access token; refreshing would only loop. */
const AUTH_PATHS = [
  "/auth/otp/request",
  "/auth/otp/verify",
  "/auth/account/delete",
  "/auth/refresh",
  "/auth/logout",
  "/auth/logout-all",
  "/auth/dev-login",
];
/** The organisation a request was made in; the api refuses it (409) when the session is elsewhere. */
const ORG_HEADER = "X-Nacs-Org";
/** The same, where a header cannot be set: the `EventSource` and every browser-followed `apiUrl(...)`. */
const ORG_QUERY = "org";
/** This client handles `organisationChanged`, so a renewal may move it instead of answering 401. */
const SESSION_MOVE_HEADER = "X-Nacs-Session-Move";

declare module "axios" {
  interface AxiosRequestConfig {
    /** Set on the retried copy of a request so a second 401 is terminal. */
    _retry?: boolean;
  }
}

/** Where every request goes: the configured origin (or none, same-origin) plus the versioned prefix. */
export const API_BASE_URL = `${import.meta.env.VITE_API_BASE_URL ?? ""}${API_PREFIX}`;

export const http = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  timeout: REQUEST_TIMEOUT_MS,
  headers: { Accept: "application/json" },
});

/** A request's path under the api prefix, without its query string. */
function apiPath(url: string | undefined): string {
  const path = (url ?? "").split("?", 1)[0] ?? "";
  return path.startsWith(API_PREFIX) ? path.slice(API_PREFIX.length) : path;
}

function isAuthPath(url: string | undefined): boolean {
  const path = apiPath(url);
  return AUTH_PATHS.includes(path);
}

/**
 * Never pinned: these act on the SESSION, not inside an organisation — sign-in, renewal, switch and
 * sign-out (`/auth/*`), `GET /me` (how a tab learns where it is after a move), creating an organisation
 * (`POST /tenants`) and answering an invitation (`/invites/:id/*`). The api's own list
 * (`shared/hooks/auth.ts` `isSessionRoute`); a pin on these would be refused if it no longer matched.
 */
function isSessionRoute(url: string | undefined): boolean {
  const path = apiPath(url);
  return path.startsWith("/auth/") || path === "/me" || path === "/tenants" || path.startsWith("/invites/");
}

// ── The organisation pin ────────────────────────────────────────────────────────────────
let organisation: string | null = null;

/**
 * The organisation this tab shows. Set by the session layer (`MeGate`) once `/me` has loaded, cleared
 * whenever the tab has none to show — signed out, between organisations, gated by a move.
 */
export function setCurrentOrg(tenantId: string | null): void {
  organisation = tenantId;
}

export function currentOrg(): string | null {
  return organisation;
}

function pinOf(config: InternalAxiosRequestConfig): string | null {
  const value = config.headers.get(ORG_HEADER);
  return typeof value === "string" && value !== "" ? value : null;
}

/** The tab moved since this request was created: it belongs to an organisation the tab no longer shows. */
function leftBehind(config: InternalAxiosRequestConfig): boolean {
  const pin = pinOf(config);
  return pin !== null && pin !== organisation;
}

/**
 * Synchronous, so the pin is the organisation the page showed when the caller made the request — not
 * whatever it shows a tick later. Set only if absent: a retried request keeps its original organisation.
 */
http.interceptors.request.use(
  (config) => {
    const method = (config.method ?? "get").toUpperCase();
    if (method !== "GET" && method !== "HEAD" && method !== "OPTIONS") {
      config.headers.set("X-Requested-With", "nacs");
    }
    if (organisation !== null && !config.headers.has(ORG_HEADER) && !isSessionRoute(config.url)) {
      config.headers.set(ORG_HEADER, organisation);
    }
    return config;
  },
  null,
  { synchronous: true },
);

// ── What the app is told ────────────────────────────────────────────────────────────────
/** Why a session ended, for the sign-in page's sentence. Any other 401 code reads as `UNAUTHORIZED`. */
export type SessionEndReason = "UNAUTHORIZED" | "MEMBERSHIP_INACTIVE" | "TENANT_DELETED";

/**
 * The tab must show another organisation. `moved`: this tab's renewal moved the session (access to the
 * old organisation ended or it was deleted). `followed`: the session is already elsewhere — another tab
 * switched or moved this browser — and this tab follows it.
 */
export type SessionMove = ({ cause: "moved" } & OrganisationChanged) | { cause: "followed" };

let unauthorizedHandler: ((reason: SessionEndReason) => void) | null = null;
let moveHandler: ((move: SessionMove) => void) | null = null;
let reloadRequiredHandler: (() => void) | null = null;
let unauthorizedInFlight = false;

/** Registered once at bootstrap; a second registration replaces the first (tests). */
export function setUnauthorizedHandler(handler: ((reason: SessionEndReason) => void) | null): void {
  unauthorizedHandler = handler;
}

/** Registered once at bootstrap: gates the screen, empties the cache and lands on the new organisation. */
export function setMoveHandler(handler: ((move: SessionMove) => void) | null): void {
  moveHandler = handler;
}

/** Registered once at bootstrap: a write the api refused as `RELOAD_REQUIRED` (a page older than the pin). */
export function setReloadRequiredHandler(handler: (() => void) | null): void {
  reloadRequiredHandler = handler;
}

function isPublicPath(): boolean {
  if (typeof window === "undefined") return true;
  const path = window.location.pathname;
  if (PUBLIC_EXACT_PATHS.includes(path.replace(/\/$/, ""))) return true;
  return PUBLIC_PATHS.some((p) => path.startsWith(p));
}

function sessionLost(reason: SessionEndReason): void {
  organisation = null;
  if (isPublicPath() || unauthorizedInFlight) return;
  unauthorizedInFlight = true;
  unauthorizedHandler?.(reason);
}

function endReasonOf(error: ApiError): SessionEndReason {
  if (error.code === "MEMBERSHIP_INACTIVE") return "MEMBERSHIP_INACTIVE";
  if (error.code === "TENANT_DELETED") return "TENANT_DELETED";
  return "UNAUTHORIZED";
}

/** Dropped, never shown: the request belonged to an organisation this tab no longer shows. */
function organisationChanged(): ApiError {
  return new ApiError({
    code: "ORGANISATION_CHANGED",
    message: "This request belonged to an organisation this tab no longer shows",
    status: null,
  });
}

/**
 * 409 `ORG_MISMATCH`: another tab switched or moved this browser while this one still showed the
 * organisation the request was made in. Nothing ran on the api. The tab follows the session — once:
 * the rest of the same burst finds the pin already gone and is only dropped.
 */
function followSession(config: InternalAxiosRequestConfig): ApiError {
  const pin = pinOf(config);
  if (pin !== null && pin === organisation) followSessionNow();
  return organisationChanged();
}

/**
 * The tab follows the session NOW: the pin goes and the move handler gates the screen, empties the cache
 * and loads wherever the session is. Also what `MeGate` calls when `/me` names another organisation than
 * the one the tab is pinned to — a missed cross-tab switch. Rendering the new organisation's screens
 * while still pinned to the old one would let a form showing B write into A if the shared cookie went
 * back to A (review 2026-10-04). Once per change: a second call finds the pin already gone.
 */
export function followSessionNow(): void {
  if (organisation === null) return;
  organisation = null;
  moveHandler?.({ cause: "followed" });
}

function retryAfterSecondsOf(error: AxiosError): number | null {
  const raw: unknown = error.response?.headers["retry-after"];
  if (typeof raw !== "string" && typeof raw !== "number") return null;
  const seconds = Number(raw);
  if (Number.isFinite(seconds)) return Math.max(0, seconds);
  const at = Date.parse(String(raw));
  return Number.isNaN(at) ? null : Math.max(0, Math.ceil((at - Date.now()) / 1000));
}

function failureFrom(error: AxiosError<ApiFailure>): ApiError {
  const body = error.response?.data;
  const status = error.response?.status ?? null;
  if (body && typeof body === "object" && body.success === false && body.error) {
    return new ApiError({
      code: body.error.code,
      message: body.error.message,
      status,
      details: body.error.details,
      requestId: body.meta?.requestId ?? null,
      retryAfterSeconds: retryAfterSecondsOf(error),
    });
  }
  if (error.code === "ECONNABORTED" || error.code === "ETIMEDOUT") {
    return new ApiError({ code: "TIMEOUT", message: "Request timed out", status });
  }
  if (axios.isCancel(error)) return new ApiError({ code: "CANCELLED", message: "Request cancelled", status });
  if (!error.response) return new ApiError({ code: "NETWORK", message: "Network error", status: null });
  return new ApiError({ code: status === 401 ? "UNAUTHORIZED" : "UNKNOWN", message: error.message, status });
}

// ── Renewal ─────────────────────────────────────────────────────────────────────────────
/**
 * What one renewal came to, shared by every request that waited on it. `renewed-elsewhere`: another tab
 * held the lock and renewed the shared cookie; `owner` (the request that started this renewal) already
 * has its answer from going again, and every other waiting request simply goes again too.
 */
type Renewal =
  | { kind: "renewed" }
  | { kind: "moved" }
  | { kind: "renewed-elsewhere"; owner: InternalAxiosRequestConfig; answer: Settled };
type Settled = { ok: true; response: AxiosResponse } | { ok: false; error: unknown };

const backoff = new RenewalBackoff();
let refreshInFlight: Promise<Renewal> | null = null;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
/** Bumped by `resetSessionClient`: a renewal from before a new sign-in must not touch the new state. */
let epoch = 0;

function stopRetrying(): void {
  backoff.reset();
  if (retryTimer !== null) clearTimeout(retryTimer);
  retryTimer = null;
  setReconnecting(false);
}

/** "Reconnecting…" means it: the next renewal is attempted on its own, so the notice clears by itself. */
function retryRenewalIn(delayMs: number): void {
  if (retryTimer !== null) clearTimeout(retryTimer);
  retryTimer = setTimeout(() => {
    retryTimer = null;
    renewSession(undefined, true).catch(() => undefined);
  }, delayMs);
}

function renewalFailed(error: ApiError): RenewalFailed {
  const failure = classifyRenewalFailure(error);
  if (failure.kind === "transient") {
    setReconnecting(true);
    retryRenewalIn(backoff.failed(failure.error));
  } else {
    stopRetrying();
    if (failure.kind === "ended") sessionLost(endReasonOf(error));
  }
  return failure;
}

/** `POST /auth/refresh`, asking to be moved rather than refused when only this organisation ended. */
async function refresh(): Promise<Renewal> {
  const mine = epoch;
  let result: RefreshResult;
  try {
    const response = await http.post<ApiSuccess<RefreshResult>>("/auth/refresh", undefined, {
      headers: { [SESSION_MOVE_HEADER]: "1" },
    });
    result = response.data.data;
  } catch (error) {
    const apiError = isApiError(error) ? error : new ApiError({ code: "UNKNOWN", message: "Unknown error" });
    if (mine !== epoch) throw classifyRenewalFailure(apiError);
    throw renewalFailed(apiError);
  }
  if (mine !== epoch) return { kind: "renewed" };
  stopRetrying();
  if (!result.organisationChanged) return { kind: "renewed" };
  // The cookies already point at the new organisation. Nothing waiting is replayed (plan §5.6): it all
  // belonged to the old one, and the move handler gates the screen before anything else can be sent.
  organisation = null;
  moveHandler?.({ cause: "moved", ...result.organisationChanged });
  return { kind: "moved" };
}

/**
 * After waiting for another tab's renewal the shared cookie is probably new already, so the request goes
 * again once before this tab renews too. Another 401 means it is not (null); anything else is the
 * request's own answer.
 */
async function goAgainAfterWaiting(config: InternalAxiosRequestConfig): Promise<Settled | null> {
  if (leftBehind(config)) return { ok: false, error: organisationChanged() };
  try {
    const response = await http.request({ ...config, _retry: true });
    stopRetrying();
    return { ok: true, response };
  } catch (error) {
    return isApiError(error) && error.status === 401 ? null : { ok: false, error };
  }
}

/**
 * One renewal at a time in this tab, and one per browser under the Web Lock. Inside the backoff wait it
 * is not attempted: the caller fails at once with the last transient error. `scheduled` is the client's
 * own retry, which the wait was for.
 */
function renewSession(trigger?: InternalAxiosRequestConfig, scheduled = false): Promise<Renewal> {
  if (refreshInFlight) return refreshInFlight;
  const waiting = scheduled ? null : backoff.blocked();
  if (waiting) return Promise.reject(new RenewalFailed("transient", waiting));
  refreshInFlight = withRenewalLock(refresh, async (): Promise<Renewal> => {
    const answer = trigger ? await goAgainAfterWaiting(trigger) : null;
    return answer ? { kind: "renewed-elsewhere", owner: trigger!, answer } : refresh();
  }).finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

/** The network is back, or the tab is shown again: renew now rather than at the end of the wait. */
function connectionBack(): void {
  if (retryTimer === null) return;
  backoff.release();
  retryRenewalIn(0);
}

if (typeof window !== "undefined") {
  window.addEventListener("online", connectionBack);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") connectionBack();
  });
}

/**
 * A new session (sign-in, switch, a new organisation, an accepted invitation, sign-out) and every test:
 * the next expired session may redirect again, nothing is pinned until `/me` says where this tab is, and
 * no wait or "Reconnecting…" from the last session survives.
 */
export function resetSessionClient(): void {
  epoch += 1;
  unauthorizedInFlight = false;
  organisation = null;
  stopRetrying();
}

function isSessionExpiry(apiError: ApiError, config: InternalAxiosRequestConfig): boolean {
  return (
    apiError.status === 401 &&
    RELOGIN_REQUIRED_CODES.has(apiError.code) &&
    !config._retry &&
    !isAuthPath(config.url)
  );
}

http.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    const apiError = axios.isAxiosError<ApiFailure>(error)
      ? failureFrom(error)
      : new ApiError({ code: "UNKNOWN", message: error instanceof Error ? error.message : "Unknown error" });
    const config = axios.isAxiosError<ApiFailure>(error) ? error.config : undefined;
    if (!config) throw apiError;
    if (apiError.status === 409 && apiError.code === "ORG_MISMATCH") throw followSession(config);
    if (apiError.status === 409 && apiError.code === "RELOAD_REQUIRED") reloadRequiredHandler?.();
    if (!isSessionExpiry(apiError, config)) throw apiError;
    // Moved since this request was created: renewing for it would retry it into another organisation.
    if (leftBehind(config)) throw organisationChanged();

    let renewal: Renewal;
    try {
      renewal = await renewSession(config);
    } catch (failure) {
      // A definite "no" keeps today's answer, the 401 (sessionLost has already gone to sign-in). Anything
      // else is its own failure — never the 401, which would send MeGate to sign-in for a blip.
      if (!(failure instanceof RenewalFailed)) {
        throw new ApiError({ code: "UNKNOWN", message: "The session could not be renewed", status: null });
      }
      throw failure.kind === "ended" ? apiError : failure.error;
    }
    if (renewal.kind === "moved") throw organisationChanged();
    if (renewal.kind === "renewed-elsewhere" && renewal.owner === config) {
      if (renewal.answer.ok) return renewal.answer.response;
      throw renewal.answer.error;
    }
    if (leftBehind(config)) throw organisationChanged();
    return http.request({ ...config, _retry: true });
  },
);

/** Full envelope, for callers that need `message` (for example the pairing confirmation). */
export async function apiRequest<T>(config: AxiosRequestConfig): Promise<ApiSuccess<T>> {
  const response = await http.request<ApiSuccess<T>>(config);
  return response.data;
}

/**
 * The URL for a GET the browser follows itself (the CSV export opens in a new tab so the stream becomes a
 * download; a selfie in an `<img>`; the live `EventSource`): same base as every other call, the cookie
 * rides along, GET is not CSRF-checked (rules/01 §7). No header can be set on these, so the organisation
 * pin goes in `?org=` — the organisation the page shows when the URL is built.
 */
export function apiUrl(path: string, params?: Record<string, unknown>): string {
  const pinned = organisation !== null && !isSessionRoute(path) ? { [ORG_QUERY]: organisation } : null;
  const all = pinned ? { ...params, ...pinned } : params;
  return http.getUri({ url: path, ...(all ? { params: all } : {}) });
}

export async function apiGet<T>(
  url: string,
  params?: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<T> {
  const envelope = await apiRequest<T>({
    method: "GET",
    url,
    ...(params ? { params } : {}),
    ...(signal ? { signal } : {}),
  });
  return envelope.data;
}

/**
 * `headers` is for the one case a caller must set one itself: `Idempotency-Key` on the ledger's four
 * money writes (ADR-0019 decision 8) — everything else about the request (cookie, CSRF, envelope,
 * refresh) is exactly the same client every other write goes through.
 */
export async function apiPost<T>(url: string, body?: unknown, headers?: Record<string, string>): Promise<T> {
  const envelope = await apiRequest<T>({ method: "POST", url, data: body, ...(headers ? { headers } : {}) });
  return envelope.data;
}

export async function apiPatch<T>(url: string, body?: unknown): Promise<T> {
  const envelope = await apiRequest<T>({ method: "PATCH", url, data: body });
  return envelope.data;
}

export async function apiPut<T>(url: string, body?: unknown): Promise<T> {
  const envelope = await apiRequest<T>({ method: "PUT", url, data: body });
  return envelope.data;
}

/** DELETE with an optional JSON body (an audited reason); no body means no Content-Type, which Fastify reads as null. */
export async function apiDelete<T>(url: string, body?: unknown): Promise<T> {
  const envelope = await apiRequest<T>({
    method: "DELETE",
    url,
    ...(body !== undefined ? { data: body } : {}),
  });
  return envelope.data;
}

/** What an upload may add to the plain call — every field optional, and the photo upload passes none. */
export interface UploadOptions {
  /**
   * Called as the bytes leave, with the share sent so far (0–1). Only when the browser knows the total;
   * a caller shows nothing rather than a bar that does not move.
   */
  onProgress?: (fraction: number) => void;
  /** Query parameters — an attachment's file name rides here, serialised like every other call's. */
  params?: Record<string, unknown>;
  /** The one header a caller sets itself: `Idempotency-Key`, the same on every retry of one attempt. */
  headers?: Record<string, string>;
  /** Overrides the 30-second upload timeout for bodies that can legitimately take longer on a slow line. */
  timeoutMs?: number;
}

/**
 * Raw binary upload through the same client (cookie, CSRF header, envelope, refresh): the face photo goes up as
 * `image/jpeg` bytes, never JSON or multipart (M3 §3), and so do profile attachments (attachments plan §3).
 * A longer timeout than JSON calls: the API validates and stores it.
 */
export async function apiUpload<T>(
  url: string,
  body: Blob,
  contentType: string,
  options: UploadOptions = {},
): Promise<T> {
  const { onProgress, params, headers, timeoutMs } = options;
  const envelope = await apiRequest<T>({
    method: "POST",
    url,
    data: body,
    headers: { ...headers, "Content-Type": contentType },
    timeout: timeoutMs ?? UPLOAD_TIMEOUT_MS,
    ...(params ? { params } : {}),
    ...(onProgress
      ? {
          onUploadProgress: (event: AxiosProgressEvent) => {
            if (event.total) onProgress(Math.min(1, event.loaded / event.total));
          },
        }
      : {}),
  });
  return envelope.data;
}
